import * as THREE from 'three';

/**
 * Where the camera stands to photograph tiles as OBJECTS — a packshot, a
 * sample board, a flat lay, a macro of an edge or a joint.
 *
 * The room framings (`resolveRoomInteriorAngle`) put a person inside a room
 * looking at a floor; asked to shoot one tile on a sweep they frame the room,
 * not the tile. These frame the pieces themselves, from their real bounding
 * box, so a named shot lands on the product whatever its size.
 */

export const OBJECT_ANGLES = ['front', 'top-down', 'three-quarter', 'low-angle', 'macro-edge', 'macro-joint'] as const;
export type ObjectAngle = typeof OBJECT_ANGLES[number];

export const isObjectAngle = (a: unknown): a is ObjectAngle => typeof a === 'string' && (OBJECT_ANGLES as readonly string[]).includes(a);

/** The lens each framing is shot with, in mm (macros long and close, the rest natural). */
export function objectLens(angle: ObjectAngle): number {
    return angle.startsWith('macro') ? 100 : angle === 'top-down' ? 50 : 60;
}

/** The f-stop: macros fall off softly, packshots are sharp edge to edge. */
export function objectAperture(angle: ObjectAngle): number {
    return angle.startsWith('macro') ? 2.8 : 11;
}

/** The pieces on screen: every visible mesh that carries the material. */
export function texturedBounds(scene: THREE.Object3D | null): THREE.Box3 | null {
    if (!scene) return null;
    const box = new THREE.Box3();
    let found = false;
    scene.updateMatrixWorld(true);
    scene.traverse((o: any) => {
        if (!o.isMesh || !o.visible) return;
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        if (!mats.some((m: any) => m?.map?.image)) return;
        box.expandByObject(o);
        found = true;
    });
    return found ? box : null;
}

/** The bounds of the given pieces (the studio's own registry of the slabs). */
export function piecesBounds(pieces: THREE.Object3D[]): THREE.Box3 | null {
    const box = new THREE.Box3();
    for (const o of pieces) {
        o.updateMatrixWorld(true);
        box.expandByObject(o);
    }
    return box.isEmpty() ? null : box;
}

/**
 * The camera for a named object framing.
 *
 * Distances come from fitting a sphere around what must be in frame to the
 * narrower of the two fields of view, so the framing holds on a portrait reel
 * and a landscape post alike.
 */
export function objectView(
    angle: ObjectAngle,
    box: THREE.Box3,
    cam: { fovDeg: number; aspect: number },
    tile: { w: number; h: number },
): { pos: [number, number, number]; target: [number, number, number] } {
    const c = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const vfov = THREE.MathUtils.degToRad(cam.fovDeg);
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * (cam.aspect || 1));
    const fit = (radius: number, margin = 1.15) => (radius * margin) / Math.sin(Math.min(vfov, hfov) / 2);
    const flat = size.y < 0.25 * Math.max(size.x, size.z);
    const at = (target: THREE.Vector3, dir: THREE.Vector3, dist: number) => {
        const p = target.clone().add(dir.normalize().multiplyScalar(dist));
        return { pos: [p.x, p.y, p.z] as [number, number, number], target: [target.x, target.y, target.z] as [number, number, number] };
    };
    const tileSize = Math.max(tile.w, tile.h);

    switch (angle) {
        case 'top-down':
            // Straight down (a hair off vertical so "up" stays defined).
            return at(c, new THREE.Vector3(0, 1, 0.001), fit(Math.max(size.x, size.z) / 2, 1.08));
        case 'three-quarter':
            return at(c, new THREE.Vector3(0.55, flat ? 0.9 : 0.45, flat ? 0.8 : -0.8), fit(size.length() / 2, 1.05));
        case 'low-angle':
            return at(c, new THREE.Vector3(0.15, flat ? 0.22 : 0.08, flat ? 1 : -1), fit(size.length() / 2, 1.05));
        case 'macro-edge': {
            // Close on the front-right corner, low, so the face and the
            // tile's real thickness are both in the picture.
            const corner = new THREE.Vector3(box.max.x - tileSize * 0.08, flat ? box.max.y : c.y, box.max.z - tileSize * 0.08);
            return at(corner, new THREE.Vector3(0.55, 0.4, 0.75), tileSize * 0.42);
        }
        case 'macro-joint':
            // The middle of the pieces — where four tiles meet on a 2×2 —
            // seen at a grazing angle so the joint lines run away from the lens.
            return at(new THREE.Vector3(c.x, box.max.y, c.z + tileSize * 0.08), new THREE.Vector3(0.12, 0.26, 1), tileSize * 0.42);
        case 'front':
        default:
            // Standing pieces face the camera; a flat piece is seen from high
            // in front, the way a tile on a table is photographed.
            return flat
                ? at(c, new THREE.Vector3(0, 1.1, 0.75), fit(Math.max(size.x, size.z) / 2, 1.2))
                // A standing slab's printed face looks toward -Z (the face is the
                // slab's +Y side, stood up by a -90° turn about X).
                : at(c, new THREE.Vector3(0, 0.06, -1), fit(Math.max(size.x, size.y) / 2, 1.18));
    }
}
